<template>
  <div class="proforma-formulario" :class="themeClass">

    <!-- ENCABEZADO -->
    <div class="d-flex align-items-center gap-2 mb-3">
      <button class="btn btn-outline-secondary btn-sm" @click="$router.push('/proformas')">
        <i class="bi bi-arrow-left"></i>
      </button>
      <div>
        <h5 class="fw-bold mb-0" style="color:var(--text-primary)">
          {{ modoEdicion ? 'Editar proforma ' + (cabecera.numero_proforma || '') : 'Nueva proforma' }}
          <span v-if="!modoEdicion && cabecera.numero_proforma"
            class="badge bg-primary ms-2 fs-6">{{ cabecera.numero_proforma }}</span>
        </h5>
        <small style="color:var(--text-secondary)">
          {{ modoEdicion ? 'Modifica los datos y guarda los cambios.' : 'Crea un presupuesto para tu cliente.' }}
        </small>
      </div>
    </div>

    <!-- ALERTA -->
    <div v-if="alerta.mensaje" :class="['alert alert-dismissible py-2 small mb-3',
      alerta.tipo === 'exito' ? 'alert-success' : alerta.tipo === 'advertencia' ? 'alert-warning' : 'alert-danger']">
      {{ alerta.mensaje }}
      <button type="button" class="btn-close" @click="alerta.mensaje = ''"></button>
    </div>

    <!-- DATOS GENERALES -->
    <div class="card mb-3" style="background:var(--secondary-bg-main);border-color:var(--border-main)">
      <div class="card-header bg-primary text-white fw-bold py-1 small">Datos generales</div>
      <div class="card-body py-2">
        <div class="row gy-2">
          <div class="col-md-3">
            <label class="form-label small mb-1">Fecha</label>
            <input v-model="cabecera.fecha_emision" type="date" class="form-control form-control-sm" />
          </div>
          <div class="col-md-3">
            <label class="form-label small mb-1">Estado</label>
            <select v-model="cabecera.estado" class="form-select form-select-sm">
              <option value="borrador">Borrador</option>
              <option value="enviada">Enviada</option>
              <option value="aceptada">Aceptada</option>
            </select>
          </div>
          <div class="col-md-6">
            <label class="form-label small mb-1">Notas / Observaciones</label>
            <input v-model="cabecera.notas" type="text" class="form-control form-control-sm"
              placeholder="Observaciones opcionales..." />
          </div>
        </div>
      </div>
    </div>

    <!-- CLIENTE -->
    <div class="card mb-3" style="background:var(--secondary-bg-main);border-color:var(--border-main)">
      <div class="card-header bg-primary text-white fw-bold py-1 small">Cliente</div>
      <div class="card-body py-2">
        <div class="row gy-2">
          <div class="col-md-6">
            <label class="form-label small mb-1">Buscar cliente (nombre o identificacion)</label>
            <div class="position-relative">
              <input v-model="busquedaCliente" type="text" class="form-control form-control-sm"
                placeholder="Escribe para buscar..." @input="buscarClientes"
                @blur="cerrarSugerenciasCliente" autocomplete="off" />
              <div v-if="buscandoCliente" class="position-absolute end-0 top-50 translate-middle-y pe-2" style="z-index:1001">
                <span class="spinner-border spinner-border-sm text-secondary" style="width:.8rem;height:.8rem"></span>
              </div>
              <ul v-if="sugerenciasCliente.length"
                class="list-group position-absolute w-100 shadow"
                style="z-index:1050;max-height:220px;overflow-y:auto;top:100%">
                <li v-for="c in sugerenciasCliente" :key="c.id"
                  class="list-group-item list-group-item-action py-1 px-2 small"
                  style="cursor:pointer" @mousedown.prevent="seleccionarCliente(c)">
                  <strong>{{ c.identification_number }}</strong> — {{ c.business_name }}
                </li>
              </ul>
            </div>
          </div>
          <div class="col-md-6" v-if="clienteSeleccionado">
            <label class="form-label small mb-1">Cliente seleccionado</label>
            <div class="d-flex align-items-center gap-2">
              <input type="text" class="form-control form-control-sm"
                :value="clienteSeleccionado.identification_number + ' - ' + clienteSeleccionado.business_name" readonly />
              <button class="btn btn-outline-primary btn-sm" @click="abrirEditarCliente" title="Editar datos del cliente">
                <i class="bi bi-pencil-fill"></i>
              </button>
              <button class="btn btn-outline-danger btn-sm" @click="limpiarCliente" title="Cambiar cliente">
                <i class="bi bi-x"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- PRODUCTOS -->
    <div class="card mb-3" style="background:var(--secondary-bg-main);border-color:var(--border-main)">
      <div class="card-header bg-primary text-white fw-bold py-1 small">Productos / Servicios</div>
      <div class="card-body py-2">
        <div class="row gy-1 mb-2">
          <div class="col-md-6">
            <label class="form-label small mb-1">Buscar producto</label>
            <input ref="inputBusquedaProducto" v-model="busquedaProducto" type="text"
              class="form-control form-control-sm" placeholder="Código principal, auxiliar o nombre..."
              @input="onBusquedaProductoInput"
              @keyup.enter.prevent="buscarProductoInmediato" />
            <ul v-if="sugerenciasProducto.length"
              class="list-group mt-1 border shadow-sm"
              style="max-height:180px;overflow-y:auto;z-index:999;position:relative">
              <li v-for="p in sugerenciasProducto" :key="p.id"
                class="list-group-item list-group-item-action small py-1 px-2"
                style="cursor:pointer" @click="agregarProducto(p)">
                <strong>{{ p.codigo_principal }}</strong> - {{ p.nombre_producto }}
              </li>
            </ul>
          </div>
        </div>

        <div class="table-responsive">
          <table class="table table-bordered table-sm align-middle small" style="color:var(--text-primary);min-width:900px">
            <thead class="table-light text-center">
              <tr>
                <th style="width:34px">#</th>
                <th style="min-width:90px">Codigo</th>
                <th style="min-width:200px">Nombre</th>
                <th style="min-width:80px">Cant.</th>
                <th style="min-width:130px">Precio</th>
                <th style="min-width:80px">% Desc.</th>
                <th style="min-width:90px">% IVA</th>
                <th style="min-width:100px">Subtotal</th>
                <th style="min-width:90px">IVA</th>
                <th style="min-width:100px">Total</th>
                <th style="width:38px"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="lineas.length === 0">
                <td colspan="11" class="text-center text-muted py-3">
                  Sin productos. Use el buscador para agregar.
                </td>
              </tr>
              <tr v-for="(linea, idx) in lineas" :key="idx">
                <td class="text-center">{{ idx + 1 }}</td>
                <td class="px-2" style="white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:100px">{{ linea.codigo }}</td>
                <td class="px-2" style="white-space:nowrap;overflow:hidden;text-overflow:ellipsis">{{ linea.nombre }}</td>
                <td>
                  <input v-model.number="linea.cantidad" type="number" min="0.001" step="0.001"
                    class="form-control form-control-sm text-end" @input="calcularLinea(linea)" />
                </td>
                <td>
                  <!-- Si tiene precios cargados, mostrar select igual que en facturas -->
                  <select v-if="linea.preciosDisponibles && linea.preciosDisponibles.length"
                    v-model="linea.precioSeleccionado"
                    class="form-select form-select-sm"
                    style="font-size:0.8rem"
                    @change="cambiarPrecioLinea(linea)">
                    <option v-for="pr in linea.preciosDisponibles" :key="pr.tipo_precio" :value="pr.tipo_precio">
                      {{ pr.tipo_precio }} — ${{ parseFloat(pr.precio).toFixed(2) }}
                    </option>
                  </select>
                  <input v-else v-model.number="linea.precio_unitario" type="number" min="0" step="0.0001"
                    class="form-control form-control-sm text-end" @input="calcularLinea(linea)" />
                </td>
                <td>
                  <input v-model.number="linea.descuento" type="number" min="0" max="100" step="0.01"
                    class="form-control form-control-sm text-end" @input="calcularLinea(linea)" />
                </td>
                <td>
                  <select v-model.number="linea.porcentaje_iva" class="form-select form-select-sm"
                    @change="calcularLinea(linea)">
                    <option :value="0">0%</option>
                    <option :value="5">5%</option>
                    <option :value="15">15%</option>
                  </select>
                </td>
                <td class="text-end fw-semibold">{{ dinero(linea.subtotal) }}</td>
                <td class="text-end">{{ dinero(linea.iva) }}</td>
                <td class="text-end fw-bold">{{ dinero(linea.total) }}</td>
                <td class="text-center">
                  <button class="btn btn-outline-danger btn-sm"
                    @click="lineas.splice(idx, 1); recalcularTotales()">
                    <i class="bi bi-trash"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

      </div>
    </div>

    <!-- RESUMEN -->
    <div class="card mb-3" style="background:var(--secondary-bg-main);border-color:var(--border-main)">
      <div class="card-body py-2">
        <div class="row justify-content-end">
          <div class="col-md-4 small">
            <div class="d-flex justify-content-between py-1 border-bottom">
              <span>Subtotal</span><strong>{{ dinero(totales.subtotal) }}</strong>
            </div>
            <!-- Desglose por tasa de IVA -->
            <template v-for="g in totales.grupos" :key="g.porcentaje">
              <div class="d-flex justify-content-between py-1 border-bottom text-muted" style="font-size:.8rem">
                <span>Base {{ g.porcentaje }}%</span><span>{{ dinero(g.base) }}</span>
              </div>
              <div class="d-flex justify-content-between py-1 border-bottom">
                <span>IVA {{ g.porcentaje }}%</span><strong>{{ dinero(g.iva) }}</strong>
              </div>
            </template>
            <div class="d-flex justify-content-between py-1">
              <span class="fw-bold text-primary">TOTAL</span>
              <strong class="text-primary fs-6">{{ dinero(totales.total) }}</strong>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ACCIONES -->
    <div class="d-flex gap-2 justify-content-end">
      <button class="btn btn-secondary" @click="$router.push('/proformas')">Cancelar</button>
      <button class="btn btn-primary" @click="guardar" :disabled="guardando">
        <span v-if="guardando" class="spinner-border spinner-border-sm me-1"></span>
        {{ guardando ? 'Guardando...' : (modoEdicion ? 'Guardar cambios' : 'Crear proforma') }}
      </button>
    </div>

    <!-- Modal editar cliente -->
    <ModalEditarCliente
      :visible="modalClienteVisible"
      :clienteId="clienteSeleccionado ? clienteSeleccionado.id : null"
      @cerrar="modalClienteVisible = false"
      @guardado="alGuardarCliente"
    />

  </div>
</template>

<script>
import api from '@/services/api'
import themeManager from '@/store/theme'
import ModalEditarCliente from '@/components/ModalEditarCliente.vue'

export default {
  name: 'ProformaNueva',
  components: { ModalEditarCliente },

  data() {
    return {
      modoEdicion: false,
      guardando: false,
      alerta: { mensaje: '', tipo: 'error' },

      cabecera: {
        numero_proforma: '',
        fecha_emision: new Date().toISOString().slice(0, 10),
        estado: 'borrador',
        notas: '',
      },

      clienteSeleccionado: null,
      busquedaCliente: '',
      sugerenciasCliente: [],
      busquedaClienteTimeout: null,
      buscandoCliente: false,

      busquedaProducto: '',
      sugerenciasProducto: [],
      busquedaProductoTimeout: null,

      lineas: [],
      totales: { subtotal: 0, total_iva: 0, total: 0, grupos: [] },
      modalClienteVisible: false,
    }
  },

  computed: {
    themeClass() {
      return themeManager.themeClass
    },
  },

  async mounted() {
    const id = this.$route.params?.id
    if (id) {
      this.modoEdicion = true
      await this.cargarProforma(id)
    } else {
      // Previsualizar el número que tendrá esta proforma
      api.get('/proformas/siguiente-numero').then(resp => {
        if (resp.data && resp.data.numero) {
          this.cabecera.numero_proforma = resp.data.numero
        }
      }).catch(() => { /* silencioso */ })
    }
  },

  methods: {
    async cargarProforma(id) {
      try {
        const { data } = await api.get('/proformas/' + id)
        const pf = data.data
        this.cabecera = {
          numero_proforma: pf.numero_proforma,
          fecha_emision:   pf.fecha_emision,
          estado:          pf.estado,
          notas:           pf.notas || '',
        }
        this.clienteSeleccionado = {
          id:             pf.cliente_id,
          identification_number: pf.identificacion_cliente,
          business_name:  pf.nombre_cliente,
        }
        this.busquedaCliente = pf.identificacion_cliente + ' - ' + pf.nombre_cliente
        // Mapear detalles base; los precios disponibles se cargan despues en paralelo
        this.lineas = (pf.detalles || []).map(function(d) {
          return {
            producto_id:        d.producto_id || null,
            codigo:             d.codigo,
            nombre:             d.nombre,
            cantidad:           parseFloat(d.cantidad),
            precio_unitario:    parseFloat(d.precio_unitario),
            descuento:          parseFloat(d.descuento),
            porcentaje_iva:     parseFloat(d.porcentaje_iva),
            subtotal:           parseFloat(d.subtotal),
            iva:                parseFloat(d.iva),
            total:              parseFloat(d.total),
            preciosDisponibles: [],
            precioSeleccionado: null,
          }
        })
        this.recalcularTotales()

        // Cargar precios disponibles de cada linea que tenga producto_id
        var lineas = this.lineas
        for (var i = 0; i < lineas.length; i++) {
          (function(linea) {
            if (!linea.producto_id) return
            api.get('/productos/' + linea.producto_id + '/precios').then(function(resp) {
              var precios = (resp.data && resp.data.data) ? resp.data.data : []
              linea.preciosDisponibles = precios
              // Buscar el precio que mas se aproxima al precio_unitario guardado
              var match = null
              for (var j = 0; j < precios.length; j++) {
                if (Math.abs(parseFloat(precios[j].precio) - linea.precio_unitario) < 0.005) {
                  match = precios[j]; break
                }
              }
              if (!match && precios.length) match = precios[0]
              if (match) linea.precioSeleccionado = match.tipo_precio
            }).catch(function() { /* silencioso: se queda con input libre */ })
          })(lineas[i])
        }
      } catch (e) {
        this.alerta = { mensaje: 'No se pudo cargar la proforma.', tipo: 'error' }
      }
    },

    buscarClientes() {
      // Si había cliente seleccionado, lo limpiamos para permitir nueva búsqueda
      if (this.clienteSeleccionado) {
        this.clienteSeleccionado = null
      }
      clearTimeout(this.busquedaClienteTimeout)
      var q = this.busquedaCliente.trim()
      if (q.length < 2) { this.sugerenciasCliente = []; return }
      var vm = this
      this.buscandoCliente = true
      this.busquedaClienteTimeout = setTimeout(async function() {
        try {
          var resp = await api.get('/clientes/autocompletar', { params: { q: q, limit: 10 } })
          vm.sugerenciasCliente = Array.isArray(resp.data?.data) ? resp.data.data : []
          if (!vm.sugerenciasCliente.length) {
            vm.alerta = { mensaje: 'No se encontraron clientes con "' + q + '".', tipo: 'advertencia' }
            setTimeout(function() { if (vm.alerta.tipo === 'advertencia') vm.alerta.mensaje = '' }, 3000)
          }
        } catch (e) {
          vm.alerta = { mensaje: 'Error al buscar clientes. Intente de nuevo.', tipo: 'error' }
        } finally {
          vm.buscandoCliente = false
        }
      }, 350)
    },

    cerrarSugerenciasCliente() {
      // Pequeño delay para permitir que el @mousedown.prevent de la sugerencia se ejecute primero
      var vm = this
      setTimeout(function() { vm.sugerenciasCliente = [] }, 200)
    },

    seleccionarCliente(c) {
      this.clienteSeleccionado = c
      this.busquedaCliente     = c.identification_number + ' - ' + c.business_name
      this.sugerenciasCliente  = []
    },

    limpiarCliente() {
      this.clienteSeleccionado = null
      this.busquedaCliente     = ''
    },

    abrirEditarCliente() {
      this.modalClienteVisible = true
    },

    alGuardarCliente(datos) {
      if (this.clienteSeleccionado) {
        this.clienteSeleccionado = {
          ...this.clienteSeleccionado,
          // Claves en español (nuevas)
          razon_social:          datos.razon_social,
          numero_identificacion: datos.numero_identificacion,
          tipo_identificacion:   datos.tipo_identificacion,
          direccion:             datos.direccion,
          telefono:              datos.telefono,
          correo:                datos.correo,
          // Alias legacy requeridos por el template hasta migración completa
          business_name:         datos.razon_social,
          identification_number: datos.numero_identificacion,
        }
      }
      this.modalClienteVisible = false
    },

    onBusquedaProductoInput() {
      clearTimeout(this.busquedaProductoTimeout)
      var q = this.busquedaProducto.trim()
      if (q.length < 2) { this.sugerenciasProducto = []; return }
      var vm = this
      this.busquedaProductoTimeout = setTimeout(function() {
        vm.ejecutarBusquedaProducto(q)
      }, 300)
    },

    buscarProductoInmediato() {
      clearTimeout(this.busquedaProductoTimeout)
      var q = this.busquedaProducto.trim()
      if (q.length < 1) { this.sugerenciasProducto = []; return }
      this.ejecutarBusquedaProducto(q)
    },

    async ejecutarBusquedaProducto(q) {
      try {
        var res = await api.get('/productos', {
          params: { filtros: JSON.stringify({ busqueda: q }), limit: 15, offset: 0 },
        })
        var lista = (res.data && res.data.data && res.data.data.productos) ? res.data.data.productos : []
        // Verificar coincidencia exacta por código (para lectores de códigos de barras)
        var qUp = q.toUpperCase()
        for (var i = 0; i < lista.length; i++) {
          var p = lista[i]
          if ((p.codigo_principal || '').toUpperCase() === qUp ||
              (p.codigo_auxiliar  || '').toUpperCase() === qUp) {
            this.agregarProducto(p)
            return
          }
        }
        this.sugerenciasProducto = lista
      } catch (e) { /* silencioso */ }
    },

    async agregarProducto(p) {
      this.sugerenciasProducto = []
      this.busquedaProducto    = ''

      var preciosDisponibles = []
      var precioSeleccionado = null
      var precioDefault = parseFloat(p.precio_unitario || 0)

      try {
        var resp = await api.get('/productos/' + p.id + '/precios')
        preciosDisponibles = (resp.data && resp.data.data) ? resp.data.data : []
        var precio1 = null
        for (var i = 0; i < preciosDisponibles.length; i++) {
          if (preciosDisponibles[i].tipo_precio === 'Precio1') { precio1 = preciosDisponibles[i]; break }
        }
        if (precio1) {
          precioDefault    = parseFloat(precio1.precio)
          precioSeleccionado = precio1.tipo_precio
        } else if (preciosDisponibles.length) {
          precioDefault    = parseFloat(preciosDisponibles[0].precio)
          precioSeleccionado = preciosDisponibles[0].tipo_precio
        }
      } catch (e) { /* usar precio_unitario */ }

      var iva   = parseFloat(p.tarifa_impuesto || 0)
      var linea = {
        producto_id:       p.id,
        codigo:            p.codigo_principal || '',
        nombre:            p.nombre_producto  || '',
        cantidad:          1,
        precio_unitario:   precioDefault,
        descuento:         0,
        porcentaje_iva:    iva,
        subtotal:          0,
        iva:               0,
        total:             0,
        preciosDisponibles: preciosDisponibles,
        precioSeleccionado: precioSeleccionado,
      }
      this.calcularLinea(linea)
      this.lineas.push(linea)
      this.recalcularTotales()
      var vm = this
      this.$nextTick(function() {
        if (vm.$refs.inputBusquedaProducto) vm.$refs.inputBusquedaProducto.focus()
      })
    },

    cambiarPrecioLinea(linea) {
      if (!linea.preciosDisponibles || !linea.precioSeleccionado) return
      var seleccionado = null
      for (var i = 0; i < linea.preciosDisponibles.length; i++) {
        if (linea.preciosDisponibles[i].tipo_precio === linea.precioSeleccionado) {
          seleccionado = linea.preciosDisponibles[i]; break
        }
      }
      if (seleccionado) {
        linea.precio_unitario = parseFloat(seleccionado.precio)
        this.calcularLinea(linea)
      }
    },

    calcularLinea(linea) {
      var base     = (linea.cantidad || 0) * (linea.precio_unitario || 0)
      var desc     = base * ((linea.descuento || 0) / 100)
      var neta     = base - desc
      var iva      = neta * ((linea.porcentaje_iva || 0) / 100)
      linea.subtotal = Math.round(neta            * 10000) / 10000
      linea.iva      = Math.round(iva             * 10000) / 10000
      linea.total    = Math.round((neta + iva)    * 10000) / 10000
      this.recalcularTotales()
    },

    recalcularTotales() {
      var subtotal = 0, totalIva = 0
      var grupos   = {}
      for (var i = 0; i < this.lineas.length; i++) {
        var l    = this.lineas[i]
        var piva = parseFloat(l.porcentaje_iva || 0)
        var key  = piva.toFixed(2)
        subtotal += l.subtotal || 0
        totalIva += l.iva      || 0
        if (!grupos[key]) grupos[key] = { porcentaje: piva, base: 0, iva: 0 }
        grupos[key].base += l.subtotal || 0
        grupos[key].iva  += l.iva      || 0
      }
      var gruposArray = Object.values(grupos)
        .map(function(g) { return { porcentaje: g.porcentaje, base: Math.round(g.base * 100) / 100, iva: Math.round(g.iva * 100) / 100 } })
        .sort(function(a, b) { return a.porcentaje - b.porcentaje })
      this.totales = {
        subtotal:  Math.round(subtotal              * 100) / 100,
        total_iva: Math.round(totalIva              * 100) / 100,
        total:     Math.round((subtotal + totalIva) * 100) / 100,
        grupos:    gruposArray,
      }
    },

    async guardar() {
      if (!this.clienteSeleccionado) {
        this.alerta = { mensaje: 'Seleccione un cliente antes de guardar.', tipo: 'error' }
        return
      }
      if (this.lineas.length === 0) {
        this.alerta = { mensaje: 'Agregue al menos un producto o servicio.', tipo: 'error' }
        return
      }
      for (var i = 0; i < this.lineas.length; i++) {
        var l = this.lineas[i]
        if (!l.nombre || !l.nombre.trim()) {
          this.alerta = { mensaje: 'El producto en la fila ' + (i + 1) + ' no tiene nombre.', tipo: 'error' }
          return
        }
        if (!l.cantidad || l.cantidad <= 0) {
          this.alerta = { mensaje: 'La cantidad en la fila ' + (i + 1) + ' debe ser mayor a 0.', tipo: 'error' }
          return
        }
      }

      this.guardando      = true
      this.alerta.mensaje = ''

      var payload = {
        cliente_id:    this.clienteSeleccionado.id,
        fecha_emision: this.cabecera.fecha_emision,
        estado:        this.cabecera.estado,
        notas:         this.cabecera.notas || null,
        productos:     this.lineas.map(function(l) {
          return {
            producto_id:     l.producto_id || null,
            codigo:          l.codigo,
            nombre:          l.nombre,
            cantidad:        l.cantidad,
            precio_unitario: l.precio_unitario,
            descuento:       l.descuento,
            porcentaje_iva:  l.porcentaje_iva,
            subtotal:        l.subtotal,
            iva:             l.iva,
            total:           l.total,
          }
        }),
      }

      try {
        if (this.modoEdicion) {
          await api.put('/proformas/' + this.$route.params.id, payload)
          this.alerta = { mensaje: 'Proforma actualizada correctamente.', tipo: 'exito' }
        } else {
          await api.post('/proformas', payload)
          this.alerta = { mensaje: 'Proforma creada correctamente.', tipo: 'exito' }
        }
        var vm = this
        setTimeout(function() { vm.$router.push('/proformas') }, 1500)
      } catch (e) {
        var msg = (e && e.response && e.response.data && e.response.data.error) || 'Error al guardar la proforma.'
        this.alerta = { mensaje: msg, tipo: 'error' }
      } finally {
        this.guardando = false
      }
    },

    dinero(valor) {
      var n = Number(valor)
      if (isNaN(n)) return '$0.00'
      return n.toLocaleString('es-EC', { style: 'currency', currency: 'USD' })
    },
  },
}
</script>

<style scoped>
.proforma-formulario {
  padding: 1.25rem;
  color: var(--text-primary);
}
</style>